import {
  Button,
  DismissIcon,
  Flex,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import { useEffect } from 'react';

import type { BloomStore } from '@jane/business-admin/types';
import { validateHTML } from '@jane/business-admin/util';

interface ScriptFormData {
  name: string;
  script: string;
  storeIds: number[];
}

interface PremiumAddExternalScriptModalProps {
  initialValues?: ScriptFormData;
  isEditing: boolean;
  isOpen: boolean;
  onClose: () => void;
  onOpenStoresModal: (currentValues: ScriptFormData) => void;
  onSubmit: (data: ScriptFormData) => void;
  stores: BloomStore[];
}

export const PremiumAddExternalScriptModal = ({
  isOpen,
  isEditing,
  initialValues,
  onClose,
  onOpenStoresModal,
  onSubmit,
  stores,
}: PremiumAddExternalScriptModalProps) => {
  const formMethods = useForm<ScriptFormData>({
    defaultValues: initialValues || {
      name: '',
      script: '',
      storeIds: [],
    },
  });

  const selectedStoreIds = formMethods.watch('storeIds') || [];

  // Use initialValues if editing, otherwise use empty defaults
  useEffect(() => {
    if (isOpen) {
      formMethods.reset(
        initialValues || {
          name: '',
          script: '',
          storeIds: [],
        }
      );
    }
  }, [isOpen, initialValues, formMethods]);

  const handleRemoveStore = (storeIdToRemove: number) => {
    const currentStoreIds = formMethods.getValues('storeIds') || [];
    const updatedStoreIds = currentStoreIds.filter(
      (id) => id !== storeIdToRemove
    );
    formMethods.setValue('storeIds', updatedStoreIds, { shouldDirty: true });
  };

  return (
    <Modal
      appId="parent"
      parentId="script-modal"
      open={isOpen}
      onRequestClose={onClose}
      overlayClose
      isLayered
      variant="standard-dialogue"
    >
      <Form.BaseForm
        formMethods={formMethods}
        name="add-script-form"
        // @ts-expect-error - FormProps types onSubmit as (data: FormData) instead of generic type that that would allow ScriptFormData
        onSubmit={onSubmit}
      >
        <Modal.Header
          nested
          title={isEditing ? 'Edit script' : 'Add script'}
          actions={<Form.SubmitButton label="Save" variant="primary" />}
        />
        <Modal.Content>
          <Form.TextField label="Name" name="name" />
          <Typography color="grays-mid" mt={12} mb={24}>
            Add a name or note. For internal use only—this will not display on
            your store's menu.
          </Typography>
          <Form.TextAreaField
            label="Script"
            name="script"
            placeholder="<script src='https://example.com/script.js'></script>"
            rows={5}
            required
            validate={validateHTML}
          />
          <Modal.ContentDivider />
          <Flex alignItems="center" justifyContent="space-between">
            <Flex flexDirection="column">
              <Typography variant="body-bold">Stores</Typography>
              <Typography variant="body" color="grays-mid" mt={12} mb={24}>
                By default, this script applies to all stores, but you can
                choose to apply it to specific stores instead.
              </Typography>
            </Flex>
            <Button
              label="Select stores"
              variant="tertiary"
              onClick={() => {
                const currentValues = formMethods.getValues();
                onOpenStoresModal(currentValues);
              }}
            />
          </Flex>
          {selectedStoreIds.length > 0 && (
            <Flex flexWrap="wrap" gap={16}>
              {selectedStoreIds.map((storeId) => {
                const store = stores.find((s) => s.id === storeId);
                return (
                  <Button
                    key={storeId}
                    label={store?.name || `Store ${storeId}`}
                    variant="secondary"
                    onClick={() => handleRemoveStore(storeId)}
                    startIcon={<DismissIcon size="sm" />}
                  />
                );
              })}
            </Flex>
          )}
        </Modal.Content>
      </Form.BaseForm>
    </Modal>
  );
};
